import { defineRule } from 'vee-validate'
import { MAX_SLIPPAGE, DEFAULT_SLIPPAGE } from '@/app/utils/constants'

export const tradeErrorMessages = {
  enoughBalance: () => 'Insufficient balance',
  insufficientBalance: () => 'Insufficient balance',
  insufficientOrderbookLiquidity: () => 'Insufficient orderbook liquidity',
  triggerPriceEqualsMarkPrice: () =>
    'The trigger price cannot be the same as the mark price',
  priceTooFarFromLastTradePrice: () =>
    'The execution price for this trade is far from the last traded price',
  orderPriceHigh: () => 'Order price is too high',
  orderPriceLow: () => 'Order price is too low',
  maxLeverage: () => 'Please decrease leverage',
  slippageExceed: () => 'Slippage % can not be higher than 50%',
  slippageTooHigh: () =>
    'Your transaction might be executed at a less desirable price if slippage % is set too high',
  slippageTooLow: () =>
    'Your transaction might not be executed if slippage % is set too low',
  tooHighPostOnlyPrice: () => 'Post-Only limit price is too high',
  tooLowPostOnlyPrice: () => 'Post-Only limit price is too low',
  minBaseAmount: (minBaseAmount: string) =>
    `Amount must be at least ${minBaseAmount}`,
  minAmount: (minNotional: string, quoteSymbol: string) =>
    `Total amount including fees should be more than ${minNotional} ${quoteSymbol}`,
  quantityTensMultiplier: (tensMultiplier: string) =>
    `Quantity must be a multiple of ${tensMultiplier}`,
  markPriceThresholdError: () =>
    'Please modify price, amount, or leverage to meet mark price requirement'
} as Record<string, any>

export const defineTradeRules = () => {
  defineRule('slippage', (value: string) => {
    const slippage = toBigNumber(value)

    if (slippage.gt(MAX_SLIPPAGE)) {
      return tradeErrorMessages.slippageExceed()
    }

    if (slippage.lt(DEFAULT_SLIPPAGE)) {
      return tradeErrorMessages.slippageTooLow()
    }

    return true
  })

  defineRule(
    'enoughBalance',
    (value: string | number, [min, max]: number[]) => {
      /* TODO: refactor this to two separate rules, one for min and the other for max */
      if (Number(value) >= Number(min) && Number(value) <= Number(max)) {
        return true
      }

      return tradeErrorMessages.enoughBalance()
    }
  )

  defineRule('minBaseAmount', (value: string, [minAmount]: string[]) => {
    if (!value || !minAmount) {
      return true
    }

    if (toBigNumber(value).lt(minAmount)) {
      return tradeErrorMessages.minBaseAmount(minAmount)
    }

    return true
  })

  defineRule(
    'minAmount',
    (value: string, [minAmount, quoteSymbol]: string[]) => {
      if (!value || !minAmount) {
        return true
      }

      if (toBigNumber(value).lte(minAmount)) {
        return tradeErrorMessages.minAmount(minAmount, quoteSymbol || '')
      }

      return true
    }
  )

  defineRule(
    'minNotional',
    (_: string, [notional, minNotional, quoteSymbol]: string[]) => {
      if (!notional || !minNotional || toBigNumber(notional).isZero()) {
        return true
      }

      if (toBigNumber(notional).lte(minNotional)) {
        return tradeErrorMessages.minAmount(minNotional, quoteSymbol || '')
      }

      return true
    }
  )

  defineRule('insufficientBalance', (value: string, max: number[]) => {
    if (!value || max[0] === undefined) {
      return true
    }

    if (toBigNumber(value).gt(max[0])) {
      return tradeErrorMessages.insufficientBalance()
    }

    return true
  })

  defineRule('insufficientBalanceCustom', (_: string, values: number[]) => {
    if (!values || values.length < 2) {
      return true
    }
    const [value, max] = values

    if (value === undefined || max === undefined) {
      return true
    }

    if (toBigNumber(value).gt(max)) {
      return tradeErrorMessages.insufficientBalance()
    }

    return true
  })

  defineRule(
    'maxOrderbookLiquidity',
    (value: string, orderbookQuantities: string[]) => {
      if (!value) {
        return true
      }

      const [orderbookQuantity] = orderbookQuantities

      if (!orderbookQuantity) {
        return true
      }

      if (toBigNumber(value).gt(orderbookQuantity)) {
        return tradeErrorMessages.insufficientOrderbookLiquidity()
      }

      return true
    }
  )

  defineRule(
    'triggerPriceEqualsMarkPrice',
    (value: string, markPrice: string) => {
      if (!value) {
        return true
      }

      if (toBigNumber(value).eq(toBigNumber(markPrice))) {
        return tradeErrorMessages.triggerPriceEqualsMarkPrice()
      }

      return true
    }
  )

  defineRule('maxLeverage', (value: string | number, [max]: [string]) => {
    const leverage = toBigNumber(value)

    if (leverage.gt(max)) {
      return tradeErrorMessages.maxLeverage()
    }

    return true
  })

  defineRule(
    'priceTooFarFromLastTradePrice',
    (value: string | number, [lastTradedPrice]: [string]) => {
      const DEFAULT_MIN_PRICE_BAND_DIFFERENCE = 20
      const DEFAULT_MAX_PRICE_BAND_DIFFERENCE = 400

      const valueInBigNumber = toBigNumber(value)

      if (valueInBigNumber.eq(0)) {
        return true
      }

      const priceDifferenceInPercentage = valueInBigNumber
        .dividedBy(lastTradedPrice)
        .times(100)

      if (
        valueInBigNumber.lte(lastTradedPrice) &&
        priceDifferenceInPercentage.lte(DEFAULT_MIN_PRICE_BAND_DIFFERENCE)
      ) {
        return tradeErrorMessages.priceTooFarFromLastTradePrice()
      }

      if (
        valueInBigNumber.gt(lastTradedPrice) &&
        priceDifferenceInPercentage.gte(DEFAULT_MAX_PRICE_BAND_DIFFERENCE)
      ) {
        return tradeErrorMessages.priceTooFarFromLastTradePrice()
      }

      return true
    }
  )

  defineRule(
    'invalidPostOnlyPrice',
    (value: string | number, [orderbookPrice, isBuy]: string[]) => {
      if (!orderbookPrice) {
        return true
      }

      const isBuyOrder = isBuy === 'true'

      if (isBuyOrder && toBigNumber(value).gte(orderbookPrice)) {
        return tradeErrorMessages.tooHighPostOnlyPrice()
      } else if (!isBuyOrder && toBigNumber(value).lte(orderbookPrice)) {
        return tradeErrorMessages.tooLowPostOnlyPrice()
      }

      return true
    }
  )

  defineRule(
    'quantityTensMultiplier',
    (value: string | number, [quantityTensMultiplier]: string[]) => {
      if (!quantityTensMultiplier) {
        return true
      }

      const tensMul = toBigNumber(10).pow(quantityTensMultiplier).toNumber()

      if (Number(value) % tensMul !== 0) {
        return tradeErrorMessages.quantityTensMultiplier(tensMul)
      }

      return true
    }
  )

  defineRule('markPriceThresholdError', (_: string, [isError]: string[]) => {
    const isMarkPriceThresholdError = isError === 'true'

    if (isMarkPriceThresholdError) {
      return tradeErrorMessages.markPriceThresholdError()
    }

    return true
  })
}
